import {createSelector} from 'reselect';

import {getCustomerConfiguration} from 'features/wizardMs/reducers/customerConfiguration/customerConfiguration';
import {getCoupon} from 'selectors/wizard/discountPrice';
import {AddToCartType} from 'pages/cart/store/actions/addToCart/addToCartType';
import {isConfigurePage} from 'selectors/routerInfo/routerInfo';
import {getPrescriptionValues} from 'features/wizardMs/selectors/prescription/prescription';
import {
    getActiveUsage,
    isFashionUsage,
    UsageItemType,
} from 'features/wizardMs/reducers/usages/usages';
import {isParentProductReaders, getActiveProduct, isSunglasses} from 'selectors/product/product.js';
import {getQuoteItemIdV2} from 'pages/cart/store/selectors/itemV2/itemV2';
import {DISTANCE, NOPRESCRIPTION, UPLOAD} from 'constants/wizard/prescriptionType';
import {NewQuoteItemPrescriptionType} from '@page/cart/store/reducers/quoteInfo/prescriptionTypes';
import {getVirtualSelectedPackage} from '../packages/packages';

/**
 * Returns quote id item to update cart
 */
export const getQuoteItemId = createSelector(
    isConfigurePage,
    getQuoteItemIdV2,
    (isConfigure, quoteItemId): {quoteItemId: number} | {} => (isConfigure ? {quoteItemId} : {}),
);

/**
 * Provides data from product section needs for add to cart
 */
export const getProductData = createSelector(
    getActiveProduct,
    getCoupon,
    getQuoteItemId,
    (
        activeProduct,
        coupon,
        quoteItemData,
    ): {productId: number; coupon: string; quoteItemId?: number} => {
        return {
            ...quoteItemData,
            productId: Number(activeProduct?.id),
            coupon,
        };
    },
);

/**
 * Provides all data related to wizard
 */
export const getWizardData = createSelector(
    getCustomerConfiguration,
    isSunglasses,
    getVirtualSelectedPackage,
    (
        customerConfiguration,
        isSunglassesFrame,
        {package: virtualPackage},
    ): Omit<AddToCartType, 'productId'> => ({
        ...(isSunglassesFrame
            ? {}
            : {
                  options: {...customerConfiguration.options, ...(virtualPackage || {})},
              }),
        quantity: customerConfiguration.quantity,
    }),
);

/**
 * Determines when the "prescription" field is needed to add to cart
 */
export const isPrescriptionFieldNeeded = createSelector(
    isParentProductReaders,
    isFashionUsage,
    isSunglasses,
    (isReaders, isFashion, isPlanoSunglasses): boolean =>
        !isReaders && !isFashion && !isPlanoSunglasses,
);

/**
 * Returns:
 * for fill it online: prescription values in prescription fields
 * for upload : 3 fields with file src, name and something else
 * for send later: nothing
 */
export const getPreparedPrescriptionForAddToCart = createSelector(
    getPrescriptionValues,
    isPrescriptionFieldNeeded,
    getActiveUsage,
    (
        prescription,
        isPrescriptionNeeded,
        activeUsage,
    ): {prescription: NewQuoteItemPrescriptionType} | {} => {
        if ((activeUsage as UsageItemType).sku === NOPRESCRIPTION || !isPrescriptionNeeded)
            return {};
        return {
            prescription: {
                method: prescription.method,
                file: prescription.method === UPLOAD ? prescription.file.fileSrc : '',
                comment: prescription.comment,
                name: prescription.name,
                od: {
                    sph: prescription.od.sph,
                    cyl: prescription.od.cyl,
                    ...((activeUsage as UsageItemType).sku === DISTANCE
                        ? {}
                        : {add: prescription.od.add}),
                    axi: prescription.od.axi,
                },
                os: {
                    sph: prescription.os.sph,
                    cyl: prescription.os.cyl,
                    ...((activeUsage as UsageItemType).sku === DISTANCE
                        ? {}
                        : {add: prescription.os.add}),
                    axi: prescription.os.axi,
                },
                pd: {
                    ...(prescription.isMultiPD
                        ? {
                              left: prescription.pd.left,
                              right: prescription.pd.right,
                          }
                        : {
                              singular: prescription.pd.singular,
                          }),
                    near: prescription.pd.near,
                },
            },
        };
    },
);

/**
 * Returns the final result of combine data to add or update cart
 */
export const getAddAndUpdateCartData = createSelector(
    getProductData,
    getWizardData,
    getPreparedPrescriptionForAddToCart,
    (productData, wizardData, prescription): AddToCartType => ({
        ...productData,
        ...wizardData,
        ...prescription,
    }),
);
